import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import {
  getDecisionNodeAtPath,
  extractHistoricalTickets,
  isLoadingUnique,
  extractDisplayingHistoricalConversationFromState,
} from '../../../../extractFromState';
import { addEdgeToDecisionTreePath, returnToPreviousMacroWizardPath } from '../../../../actions/searchActions';
import { openPreviousConversationsSection, selectTicket } from 'actions';
import { BackButton, NextButton } from '../../../common/buttonsComponents';
import {
  Container,
  SubText,
  Checkbox,
  CheckboxAndLabelContainer,
  ThumbsUpIcon,
  ThumbsUpIconLabel,
  InnerContainer,
} from './DTPreviousConversationsCheckNode.styles';
import { ActionsButtonsWrapper, Title, TopContainer } from '../../DecisionTree.styles';

import LoadingComponent, { LoadingWrapper } from 'components/LoadingComponent/LoadingComponent';
import { activateHistoricalTicket } from 'actions/tools/ToolActions';
import { getPositiveIntOrNullFromString } from 'util/formatUtil';
import { collectAndHydrateTicketsOnSameChannel } from '../../../../actions/ticketActions';
import { handleError } from '../../../../services/ErrorService/ErrorService';
import { setInProgressTreeAndWizardLocation } from 'actions/inProgressTreeAndWizardLocationActions';
import { SELECT_TICKET_LOADING } from 'constants/loading';

const DTPreviousConversationsCheckNode = () => {
  const dispatch = useDispatch();
  const decisionNode = useSelector(getDecisionNodeAtPath);
  const historicalTickets = useSelector(extractHistoricalTickets);
  const isDisplayingHistoricalConversation = useSelector(extractDisplayingHistoricalConversationFromState);
  const scrollTracking = useSelector((state) => state.ui.scrollTracking);
  const daysToEnforce = getPositiveIntOrNullFromString(decisionNode?.daysToEnforce);
  const numberOfMessages = getPositiveIntOrNullFromString(decisionNode?.numberOfMessages);

  const [isLoading, setIsLoading] = useState(true);
  const isTicketStateLoading = useSelector((state) => isLoadingUnique(state, SELECT_TICKET_LOADING));
  const showLoading = isLoading || isTicketStateLoading;

  const requiredHistoricalTickets = useMemo(() => {
    if (daysToEnforce === null || !Array.isArray(historicalTickets?.allTickets)) {
      return [];
    }

    // filter by dates first, then by numberOfMessages if it is defined, then if not yet read
    return historicalTickets.allTickets
      .filter((id, index) => {
        const dateTicketCreated = new Date(historicalTickets[id].createdAt);
        const lastDateOfEnforcement = new Date().setDate(new Date().getDate() - daysToEnforce);

        // historical tickets are sorted in array, so if numberOfMessages is defined, we only get messages with lower index
        return dateTicketCreated >= lastDateOfEnforcement && (!numberOfMessages || index < numberOfMessages);
      })
      .map((id) => ({ ...historicalTickets[id], seen: !!scrollTracking[id] }));
  }, [daysToEnforce, historicalTickets, scrollTracking, numberOfMessages]);

  const isNextButtonActive = () => {
    return (
      requiredHistoricalTickets.every((ticket) => ticket.seen) && !isLoading && !isDisplayingHistoricalConversation
    );
  };

  const openPreviousConversation = (ticket) => {
    dispatch(setInProgressTreeAndWizardLocation());
    dispatch(activateHistoricalTicket());
    dispatch(selectTicket(ticket.ticketId, 'HISTORICAL', true, ticket.isExternalHistoricalTicket, false));
  };

  const handleNextButton = useCallback(() => {
    dispatch(addEdgeToDecisionTreePath('all_conversations_read'));
  }, [dispatch]);

  useEffect(() => {
    if (isEmpty(requiredHistoricalTickets) && !isLoading) {
      handleNextButton();
    }
  }, [requiredHistoricalTickets, isLoading, handleNextButton]);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        await dispatch(collectAndHydrateTicketsOnSameChannel());
        dispatch(openPreviousConversationsSection());
      } catch (error) {
        handleError({ error, showPopup: true, customMessage: 'There was a problem with loading tickets history' });
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch]);

  return showLoading ? (
    <LoadingWrapper>
      <LoadingComponent delay={0} marginTop={'0px'} />
    </LoadingWrapper>
  ) : (
    <TopContainer>
      <Container>
        {decisionNode.title && <Title>{decisionNode.title}</Title>}
        {decisionNode.subText && <SubText>{decisionNode.subText}</SubText>}
        <InnerContainer>
          <p>Please read the conversations from the following dates:</p>
          {requiredHistoricalTickets.map((ticket, i) => (
            <CheckboxAndLabelContainer key={`requiredHistoricalTickets-${i}`}>
              <Checkbox id={`checkbox-${i}`} checked={ticket.seen} readOnly />
              <label htmlFor={`checkbox-${i}`}>{moment(ticket.createdAt).format('MM/DD/YYYY')}</label>
              <span onClick={() => openPreviousConversation(ticket)}>Read conversation</span>
            </CheckboxAndLabelContainer>
          ))}
          {isNextButtonActive() && (
            <CheckboxAndLabelContainer>
              <ThumbsUpIcon src="/images/thumbs-up.svg" alt="" />
              <ThumbsUpIconLabel>
                Good job, you read them all! Please click next and move to the next step.
              </ThumbsUpIconLabel>
            </CheckboxAndLabelContainer>
          )}
        </InnerContainer>
      </Container>
      <ActionsButtonsWrapper>
        <NextButton
          disabled={!isNextButtonActive()}
          active={isNextButtonActive()}
          type="submit"
          onClick={handleNextButton}
        >
          NEXT
        </NextButton>
        <BackButton
          disabled={isDisplayingHistoricalConversation}
          active={!isDisplayingHistoricalConversation}
          onClick={() => dispatch(returnToPreviousMacroWizardPath())}
        >
          BACK
        </BackButton>
      </ActionsButtonsWrapper>
    </TopContainer>
  );
};

export default DTPreviousConversationsCheckNode;
